import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  MessageSquare,
  Send,
  Download,
  FileText,
  ArrowRight,
  DollarSign,
  Calendar,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Eye,
  Copy,
  Building,
  User,
  Clock,
  Hash,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Filter,
  LayoutGrid,
  List,
} from 'lucide-react';
import { getSeverityColor, getStatusColor } from '../utils/styleUtils';
import DocumentManager from '../components/DocumentManager';

export const AlertDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [transacciones, setTransacciones] = useState([]);
  const [loadingTransacciones, setLoadingTransacciones] = useState(false);
  const [showTransacciones, setShowTransacciones] = useState(true);
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' | 'list'
  const [expandedDates, setExpandedDates] = useState({});
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionDetail, setShowTransactionDetail] = useState(false);

  useEffect(() => {
    loadAlertData();
  }, [id]);

  const loadAlertData = async () => {
    try {
      const [alertRes, commentsRes] = await Promise.all([
        api.get(`/alerts/${id}`),
        api.get(`/alerts/${id}/comments`),
      ]);
      setAlert(alertRes.data);
      setComments(commentsRes.data);
      
      // Cargar detalles de transacciones si hay movimientos_ids
      if (alertRes.data.movimientos_ids && alertRes.data.movimientos_ids.length > 0) {
        loadTransacciones(alertRes.data.movimientos_ids);
      }
    } catch (error) {
      toast.error('Error al cargar la alerta');
    } finally {
      setLoading(false);
    }
  };
  
  const loadTransacciones = async (movimientosIds) => {
    if (!movimientosIds || movimientosIds.length === 0) return;
    
    setLoadingTransacciones(true);
    try {
      const response = await api.post('/movimientos/by-ids', movimientosIds);
      setTransacciones(response.data);
      // Expandir todas las fechas por defecto
      const dates = {};
      response.data.forEach(tx => {
        if (tx.fecha) {
          const date = new Date(tx.fecha).toLocaleDateString('es-AR');
          dates[date] = true;
        }
      });
      setExpandedDates(dates);
    } catch (error) {
      console.error('Error cargando transacciones:', error);
      // Si falla el endpoint, intentar cargar una por una (fallback)
      try {
        const results = await Promise.all(
          movimientosIds.slice(0, 20).map(async (movId) => {
            try {
              const res = await api.get(`/movimientos/${movId}`);
              return res.data;
            } catch {
              return null;
            }
          })
        );
        setTransacciones(results.filter(Boolean));
      } catch {
        console.error('Error en fallback de transacciones');
      }
    } finally {
      setLoadingTransacciones(false);
    }
  };
  
  const formatMonto = (monto) => {
    if (!monto) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(monto);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  // Agrupar transacciones por fecha
  const transaccionesPorFecha = useMemo(() => {
    const grouped = {};
    transacciones.forEach(tx => {
      const fecha = tx.fecha ? new Date(tx.fecha).toLocaleDateString('es-AR') : 'Sin fecha';
      if (!grouped[fecha]) {
        grouped[fecha] = {
          fecha,
          fechaObj: tx.fecha ? new Date(tx.fecha) : null,
          transacciones: [],
          totalMonto: 0,
          totalEntradas: 0,
          totalSalidas: 0
        };
      }
      grouped[fecha].transacciones.push(tx);
      grouped[fecha].totalMonto += tx.monto || 0;
      // Determinar si es entrada o salida basado en alguna lógica
      if (tx.movimiento?.includes('ENTRADA') || tx.movimiento?.includes('CREDITO')) {
        grouped[fecha].totalEntradas += tx.monto || 0;
      } else {
        grouped[fecha].totalSalidas += tx.monto || 0;
      }
    });
    
    // Ordenar por fecha más reciente primero
    return Object.values(grouped).sort((a, b) => {
      if (!a.fechaObj) return 1;
      if (!b.fechaObj) return -1;
      return b.fechaObj - a.fechaObj;
    });
  }, [transacciones]);

  // Estadísticas generales
  const stats = useMemo(() => {
    const totalMonto = transacciones.reduce((sum, tx) => sum + (tx.monto || 0), 0);
    const promedio = transacciones.length > 0 ? totalMonto / transacciones.length : 0;
    const maximo = transacciones.length > 0 ? Math.max(...transacciones.map(tx => tx.monto || 0)) : 0;
    const minimo = transacciones.length > 0 ? Math.min(...transacciones.filter(tx => tx.monto > 0).map(tx => tx.monto)) : 0;
    
    // Contar días únicos
    const diasUnicos = new Set(transacciones.map(tx => 
      tx.fecha ? new Date(tx.fecha).toLocaleDateString() : 'N/A'
    )).size;
    
    return { totalMonto, promedio, maximo, minimo, diasUnicos };
  }, [transacciones]);

  const toggleDateExpansion = (fecha) => {
    setExpandedDates(prev => ({ ...prev, [fecha]: !prev[fecha] }));
  };

  const expandAll = () => {
    const all = {};
    transaccionesPorFecha.forEach(g => { all[g.fecha] = true; });
    setExpandedDates(all);
  };

  const collapseAll = () => {
    setExpandedDates({});
  };

  const handleTomar = async () => {
    try {
      await api.post(`/alerts/${id}/tomar`);
      toast.success('Alerta tomada correctamente');
      loadAlertData();
    } catch (error) {
      toast.error('Error al tomar la alerta');
    }
  };

  const handleCerrar = async () => {
    const resolucion = prompt('Ingrese la resolución de la alerta:');
    if (!resolucion) return;

    try {
      await api.post(
        `/alerts/${id}/cerrar`,
        null,
        { params: { resolucion } }
      );
      toast.success('Alerta cerrada correctamente');
      loadAlertData();
    } catch (error) {
      toast.error('Error al cerrar la alerta');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await api.post(
        `/alerts/${id}/comments`,
        null,
        { params: { comment_text: newComment } }
      );
      setNewComment('');
      toast.success('Comentario agregado');
      loadAlertData();
    } catch (error) {
      toast.error('Error al agregar comentario');
    }
  };

  const handleDownloadDossier = async () => {
    try {
      toast.info('Generando dossier PDF...');
      const response = await api.get(`/exports/alerts/${id}/dossier`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dossier_alerta_${id.slice(0,8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Dossier descargado correctamente');
    } catch (error) {
      toast.error('Error al generar el dossier');
    }
  };

  // Se usan getSeverityColor y getStatusColor importados de utils/styleUtils

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando alerta...</p>
        </div>
      </div>
    );
  }

  if (!alert) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-slate-600">Alerta no encontrada</p>
          <Button onClick={() => navigate('/m/monitoreo-transaccional/alerts')} className="mt-4">
            Volver a Alertas
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto">
      {/* Header */}
      <div className="flex items-center mb-6">
        <Button
          data-testid="back-button"
          variant="ghost"
          onClick={() => navigate('/m/monitoreo-transaccional/alerts')}
          className="mr-4"
        >
          <ArrowLeft size={20} />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-slate-900">
            Detalle de Alerta
          </h1>
          <p className="text-sm text-slate-500 font-mono mt-1">ID: {alert.id}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Alert Info */}
          <Card className="p-6 border border-border">
            <div className="flex items-start justify-between mb-4">
              <div className="space-x-2">
                <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold ${getSeverityColor(alert.severidad)}`}>
                  {alert.severidad}
                </span>
                <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(alert.estado)}`}>
                  {alert.estado.replace('_', ' ')}
                </span>
              </div>
              <div className="flex space-x-2">
                {alert.estado === 'NUEVA' && (
                  <Button
                    data-testid="tomar-alert-button"
                    onClick={handleTomar}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <CheckCircle size={16} className="mr-2" />
                    Tomar Alerta
                  </Button>
                )}
                {['TOMADA', 'EN_INVESTIGACION'].includes(alert.estado) && (
                  <Button
                    data-testid="cerrar-alert-button"
                    onClick={handleCerrar}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle size={16} className="mr-2" />
                    Cerrar Alerta
                  </Button>
                )}
              </div>
            </div>
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-3">
              {alert.titulo}
            </h2>
            <p className="text-slate-600 mb-4">{alert.descripcion}</p>
            {alert.resolucion && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm font-medium text-green-900 mb-1">
                  Resolución:
                </p>
                <p className="text-sm text-green-800">{alert.resolucion}</p>
              </div>
            )}
          </Card>

          {/* Transacciones Detalladas - MEJORADO */}
          {alert.movimientos_ids && alert.movimientos_ids.length > 0 && (
            <Card className="p-6 border border-border">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowTransacciones(!showTransacciones)}
              >
                <h3 className="text-lg font-heading font-bold text-slate-900 flex items-center">
                  <DollarSign size={20} className="mr-2 text-green-600" />
                  Transacciones Asociadas
                  <Badge variant="outline" className="ml-2">
                    {alert.movimientos_ids.length}
                  </Badge>
                </h3>
                <Button variant="ghost" size="sm">
                  {showTransacciones ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </Button>
              </div>
              
              {showTransacciones && (
                <div className="mt-4">
                  {loadingTransacciones ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-3 text-slate-600">Cargando transacciones...</span>
                    </div>
                  ) : transacciones.length > 0 ? (
                    <>
                      {/* Resumen estadístico mejorado */}
                      <div className="grid grid-cols-5 gap-3 mb-6">
                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                          <p className="text-xs text-blue-600 font-medium">Total Transacciones</p>
                          <p className="text-xl font-bold text-blue-900">{transacciones.length}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                          <p className="text-xs text-green-600 font-medium">Monto Total</p>
                          <p className="text-xl font-bold text-green-900">
                            {formatMonto(stats.totalMonto)}
                          </p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                          <p className="text-xs text-purple-600 font-medium">Promedio</p>
                          <p className="text-xl font-bold text-purple-900">
                            {formatMonto(stats.promedio)}
                          </p>
                        </div>
                        <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                          <p className="text-xs text-amber-600 font-medium">Máximo</p>
                          <p className="text-xl font-bold text-amber-900">
                            {formatMonto(stats.maximo)}
                          </p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <p className="text-xs text-slate-600 font-medium">Días con Mov.</p>
                          <p className="text-xl font-bold text-slate-900">{stats.diasUnicos}</p>
                        </div>
                      </div>
                      
                      {/* Controles de vista */}
                      <div className="flex items-center justify-between mb-4 pb-3 border-b">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">Vista:</span>
                          <div className="flex rounded-lg border overflow-hidden">
                            <Button
                              variant={viewMode === 'grouped' ? 'default' : 'ghost'}
                              size="sm"
                              className={`rounded-none ${viewMode === 'grouped' ? 'bg-slate-900' : ''}`}
                              onClick={() => setViewMode('grouped')}
                            >
                              <LayoutGrid size={14} className="mr-1" />
                              Agrupado
                            </Button>
                            <Button
                              variant={viewMode === 'list' ? 'default' : 'ghost'}
                              size="sm"
                              className={`rounded-none ${viewMode === 'list' ? 'bg-slate-900' : ''}`}
                              onClick={() => setViewMode('list')}
                            >
                              <List size={14} className="mr-1" />
                              Lista
                            </Button>
                          </div>
                        </div>
                        {viewMode === 'grouped' && (
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={expandAll}>
                              Expandir Todo
                            </Button>
                            <Button variant="outline" size="sm" onClick={collapseAll}>
                              Colapsar Todo
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {viewMode === 'grouped' ? (
                        /* Vista Agrupada por Fecha */
                        <div className="space-y-3">
                          {transaccionesPorFecha.map((grupo) => (
                            <div key={grupo.fecha} className="border rounded-lg overflow-hidden">
                              <div 
                                className="flex items-center justify-between p-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                                onClick={() => toggleDateExpansion(grupo.fecha)}
                              >
                                <div className="flex items-center gap-3">
                                  <Calendar size={16} className="text-slate-500" />
                                  <span className="font-semibold text-slate-900">{grupo.fecha}</span>
                                  <Badge variant="secondary" className="text-xs">
                                    {grupo.transacciones.length} transacciones
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-4">
                                  <span className="text-sm font-semibold text-green-700">
                                    {formatMonto(grupo.totalMonto)}
                                  </span>
                                  {expandedDates[grupo.fecha] ? 
                                    <ChevronUp size={18} className="text-slate-400" /> : 
                                    <ChevronDown size={18} className="text-slate-400" />
                                  }
                                </div>
                              </div>
                              
                              {expandedDates[grupo.fecha] && (
                                <div className="border-t">
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="bg-slate-50/50">
                                        <TableHead className="text-xs w-[80px]">Hora</TableHead>
                                        <TableHead className="text-xs">ID</TableHead>
                                        <TableHead className="text-xs">Origen</TableHead>
                                        <TableHead className="text-xs">Destino</TableHead>
                                        <TableHead className="text-xs text-right">Monto</TableHead>
                                        <TableHead className="text-xs">Tipo</TableHead>
                                        <TableHead className="text-xs w-[60px]"></TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {grupo.transacciones.map((tx, idx) => (
                                        <TableRow 
                                          key={tx.id_waya || idx} 
                                          className="hover:bg-blue-50/50 cursor-pointer transition-colors"
                                          onClick={() => { setSelectedTransaction(tx); setShowTransactionDetail(true); }}
                                        >
                                          <TableCell className="text-xs font-mono text-slate-500">
                                            {tx.fecha ? new Date(tx.fecha).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                                          </TableCell>
                                          <TableCell className="text-xs">
                                            <div className="flex items-center gap-1">
                                              <span className="font-mono text-slate-600">
                                                {tx.id_waya?.slice(0, 10)}...
                                              </span>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-5 w-5 p-0"
                                                onClick={(e) => { e.stopPropagation(); copyToClipboard(tx.id_waya); }}
                                              >
                                                <Copy size={10} />
                                              </Button>
                                            </div>
                                          </TableCell>
                                          <TableCell className="text-xs">
                                            <div>
                                              <p className="font-mono text-slate-700 truncate max-w-[150px]" title={tx.nombre_origen || tx.cbu_cvu_origen}>
                                                {tx.nombre_origen || tx.cbu_cvu_origen?.slice(0, 12) + '...'}
                                              </p>
                                              {tx.cuit_cuil_origen && (
                                                <p className="text-slate-400 text-xs">{tx.cuit_cuil_origen}</p>
                                              )}
                                            </div>
                                          </TableCell>
                                          <TableCell className="text-xs">
                                            <div className="flex items-center gap-1">
                                              <ArrowRight size={12} className="text-slate-300 flex-shrink-0" />
                                              <div>
                                                <p className="font-mono text-slate-700 truncate max-w-[150px]" title={tx.nombre_destinatario || tx.cbu_cvu_destino}>
                                                  {tx.nombre_destinatario || tx.cbu_cvu_destino?.slice(0, 12) + '...'}
                                                </p>
                                                {tx.banco_destinatario && (
                                                  <p className="text-slate-400 text-xs truncate max-w-[100px]">{tx.banco_destinatario}</p>
                                                )}
                                              </div>
                                            </div>
                                          </TableCell>
                                          <TableCell className="text-xs text-right">
                                            <span className="font-semibold text-green-700">
                                              {formatMonto(tx.monto)}
                                            </span>
                                          </TableCell>
                                          <TableCell className="text-xs">
                                            <Badge variant="outline" className="text-xs">
                                              {tx.movimiento || 'N/A'}
                                            </Badge>
                                          </TableCell>
                                          <TableCell>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-6 w-6 p-0"
                                              onClick={(e) => { e.stopPropagation(); setSelectedTransaction(tx); setShowTransactionDetail(true); }}
                                            >
                                              <Eye size={14} />
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        /* Vista Lista Simple */
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50">
                                <TableHead className="text-xs">Fecha/Hora</TableHead>
                                <TableHead className="text-xs">ID</TableHead>
                                <TableHead className="text-xs">Origen</TableHead>
                                <TableHead className="text-xs">Destino</TableHead>
                                <TableHead className="text-xs text-right">Monto</TableHead>
                                <TableHead className="text-xs">Tipo</TableHead>
                                <TableHead className="text-xs w-[60px]"></TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {transacciones.map((tx, idx) => (
                                <TableRow 
                                  key={tx.id_waya || idx} 
                                  className="hover:bg-blue-50/50 cursor-pointer"
                                  onClick={() => { setSelectedTransaction(tx); setShowTransactionDetail(true); }}
                                >
                                  <TableCell className="text-xs">
                                    <div className="flex items-center gap-1">
                                      <Calendar size={12} className="text-slate-400" />
                                      {tx.fecha ? new Date(tx.fecha).toLocaleString('es-AR') : '-'}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-xs font-mono text-slate-600">
                                    {tx.id_waya?.slice(0, 12)}...
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    <div className="space-y-0.5">
                                      <p className="font-mono text-slate-700 truncate max-w-[120px]" title={tx.cbu_cvu_origen}>
                                        {tx.cbu_cvu_origen?.slice(0, 8)}...
                                      </p>
                                      {tx.cuit_cuil_origen && (
                                        <p className="text-slate-500">CUIT: {tx.cuit_cuil_origen}</p>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    <div className="flex items-center gap-1">
                                      <ArrowRight size={12} className="text-slate-400 flex-shrink-0" />
                                      <div className="space-y-0.5">
                                        <p className="font-mono text-slate-700 truncate max-w-[120px]" title={tx.cbu_cvu_destino}>
                                          {tx.cbu_cvu_destino?.slice(0, 8)}...
                                        </p>
                                        {tx.banco_destinatario && (
                                          <p className="text-slate-500 truncate max-w-[100px]">{tx.banco_destinatario}</p>
                                        )}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-xs text-right font-semibold text-green-700">
                                    {formatMonto(tx.monto)}
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    <Badge variant="outline" className="text-xs">
                                      {tx.movimiento || 'N/A'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                      <Eye size={14} />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-6 text-slate-500">
                      <p className="text-sm">No se pudieron cargar los detalles de las transacciones</p>
                      <p className="text-xs mt-1">IDs registrados: {alert.movimientos_ids.length}</p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Modal de Detalle de Transacción */}
          <Dialog open={showTransactionDetail} onOpenChange={setShowTransactionDetail}>
            <DialogContent className="max-w-2xl" style={{ backgroundColor: '#ffffff' }}>
              {selectedTransaction && (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <DollarSign className="text-green-600" />
                      Detalle de Transacción
                    </DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4 mt-4">
                    {/* Monto destacado */}
                    <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-green-600">Monto</p>
                      <p className="text-3xl font-bold text-green-700">{formatMonto(selectedTransaction.monto)}</p>
                    </div>
                    
                    {/* Información en grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <Card className="p-4 bg-slate-50">
                        <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-2">Origen</h4>
                        <div className="space-y-2">
                          {selectedTransaction.nombre_origen && (
                            <p className="font-medium text-slate-900">{selectedTransaction.nombre_origen}</p>
                          )}
                          {selectedTransaction.cuit_cuil_origen && (
                            <div className="flex items-center gap-2">
                              <User size={14} className="text-slate-400" />
                              <span className="text-sm">{selectedTransaction.cuit_cuil_origen}</span>
                              <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => copyToClipboard(selectedTransaction.cuit_cuil_origen)}>
                                <Copy size={10} />
                              </Button>
                            </div>
                          )}
                          {selectedTransaction.cbu_cvu_origen && (
                            <div className="flex items-center gap-2">
                              <CreditCard size={14} className="text-slate-400" />
                              <span className="text-xs font-mono">{selectedTransaction.cbu_cvu_origen}</span>
                              <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => copyToClipboard(selectedTransaction.cbu_cvu_origen)}>
                                <Copy size={10} />
                              </Button>
                            </div>
                          )}
                        </div>
                      </Card>
                      
                      <Card className="p-4 bg-slate-50">
                        <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-2">Destino</h4>
                        <div className="space-y-2">
                          {selectedTransaction.nombre_destinatario && (
                            <p className="font-medium text-slate-900">{selectedTransaction.nombre_destinatario}</p>
                          )}
                          {selectedTransaction.cuit_cuil_destino && (
                            <div className="flex items-center gap-2">
                              <User size={14} className="text-slate-400" />
                              <span className="text-sm">{selectedTransaction.cuit_cuil_destino}</span>
                              <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => copyToClipboard(selectedTransaction.cuit_cuil_destino)}>
                                <Copy size={10} />
                              </Button>
                            </div>
                          )}
                          {selectedTransaction.cbu_cvu_destino && (
                            <div className="flex items-center gap-2">
                              <CreditCard size={14} className="text-slate-400" />
                              <span className="text-xs font-mono">{selectedTransaction.cbu_cvu_destino}</span>
                              <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => copyToClipboard(selectedTransaction.cbu_cvu_destino)}>
                                <Copy size={10} />
                              </Button>
                            </div>
                          )}
                          {selectedTransaction.banco_destinatario && (
                            <div className="flex items-center gap-2">
                              <Building size={14} className="text-slate-400" />
                              <span className="text-sm">{selectedTransaction.banco_destinatario}</span>
                            </div>
                          )}
                        </div>
                      </Card>
                    </div>
                    
                    {/* Información adicional */}
                    <Card className="p-4 bg-slate-50">
                      <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-3">Información Adicional</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Hash size={14} className="text-slate-400" />
                          <span className="text-slate-600">ID:</span>
                          <span className="font-mono text-xs">{selectedTransaction.id_waya}</span>
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => copyToClipboard(selectedTransaction.id_waya)}>
                            <Copy size={10} />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar size={14} className="text-slate-400" />
                          <span className="text-slate-600">Fecha:</span>
                          <span>{selectedTransaction.fecha ? new Date(selectedTransaction.fecha).toLocaleString('es-AR') : '-'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock size={14} className="text-slate-400" />
                          <span className="text-slate-600">Tipo:</span>
                          <Badge variant="outline">{selectedTransaction.movimiento || 'N/A'}</Badge>
                        </div>
                        {selectedTransaction.concepto && (
                          <div className="flex items-center gap-2 col-span-2">
                            <FileText size={14} className="text-slate-400" />
                            <span className="text-slate-600">Concepto:</span>
                            <span>{selectedTransaction.concepto}</span>
                          </div>
                        )}
                        {selectedTransaction.referencia && (
                          <div className="flex items-center gap-2 col-span-2">
                            <Hash size={14} className="text-slate-400" />
                            <span className="text-slate-600">Referencia:</span>
                            <span className="font-mono text-xs">{selectedTransaction.referencia}</span>
                          </div>
                        )}
                      </div>
                    </Card>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* Comments */}
          <Card className="p-6 border border-border">
            <h3 className="text-lg font-heading font-bold text-slate-900 mb-4 flex items-center">
              <MessageSquare size={20} className="mr-2" />
              Comentarios
            </h3>
            <div className="space-y-4 mb-4">
              {comments.length === 0 ? (
                <p className="text-sm text-slate-500">No hay comentarios</p>
              ) : (
                comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="p-3 bg-slate-50 rounded-md border border-slate-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-900">
                        {comment.usuario_nombre || 'Usuario'}
                      </span>
                      <span className="text-xs text-slate-500">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700">{comment.comentario}</p>
                  </div>
                ))
              )}
            </div>
            <div className="flex space-x-2">
              <Textarea
                data-testid="comment-input"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Agregar un comentario..."
                className="flex-1 bg-slate-50"
                rows={3}
              />
              <Button
                data-testid="add-comment-button"
                onClick={handleAddComment}
                disabled={!newComment.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Send size={16} />
              </Button>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Metadata */}
          <Card className="p-6 border border-border">
            <h3 className="text-sm font-heading font-bold text-slate-900 mb-4 uppercase tracking-wider">
              Información
            </h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-slate-500 uppercase tracking-wider">
                  Creada
                </dt>
                <dd className="text-sm text-slate-900 mt-1 font-mono">
                  {new Date(alert.created_at).toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500 uppercase tracking-wider">
                  Actualizada
                </dt>
                <dd className="text-sm text-slate-900 mt-1 font-mono">
                  {new Date(alert.updated_at).toLocaleString()}
                </dd>
              </div>
              {alert.cuenta_id && (
                <div>
                  <dt className="text-xs text-slate-500 uppercase tracking-wider">
                    Cuenta
                  </dt>
                  <dd className="text-sm text-slate-900 mt-1 font-mono">
                    {alert.cuenta_id}
                  </dd>
                </div>
              )}
              {alert.monitor_id && (
                <div>
                  <dt className="text-xs text-slate-500 uppercase tracking-wider">
                    Monitor
                  </dt>
                  <dd className="text-sm text-slate-900 mt-1 font-mono">
                    {alert.monitor_id}
                  </dd>
                </div>
              )}
            </dl>
          </Card>

          {/* Acciones */}
          <Card className="p-6 border border-border">
            <h3 className="text-sm font-heading font-bold text-slate-900 mb-4 uppercase tracking-wider">
              Acciones
            </h3>
            <Button
              data-testid="download-dossier-button"
              onClick={handleDownloadDossier}
              className="w-full bg-slate-800 hover:bg-slate-900 text-white"
            >
              <Download size={16} className="mr-2" />
              Descargar Dossier PDF
            </Button>
          </Card>

          {/* Resumen de Movimientos */}
          {alert.movimientos_ids && alert.movimientos_ids.length > 0 && (
            <Card className="p-6 border border-border bg-gradient-to-br from-green-50 to-emerald-50">
              <h3 className="text-sm font-heading font-bold text-slate-900 mb-3 uppercase tracking-wider flex items-center">
                <DollarSign size={16} className="mr-2 text-green-600" />
                Transacciones
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Total</span>
                  <Badge className="bg-green-600 text-white">
                    {alert.movimientos_ids.length}
                  </Badge>
                </div>
                {transacciones.length > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Monto Total</span>
                    <span className="font-semibold text-green-700">
                      {formatMonto(transacciones.reduce((sum, t) => sum + (t.monto || 0), 0))}
                    </span>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Ver detalles en la sección principal
              </p>
            </Card>
          )}

          {/* Documentos Adjuntos */}
          <DocumentManager entityType="alert" entityId={id} />
        </div>
      </div>
    </div>
  );
};

export default AlertDetail;
